import React, { useEffect, useState, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import withRouter from "components/Common/withRouter"
import { useFormik } from "formik"
import * as Yup from "yup"
import { formatNumberWithCommas } from "../../common/utility"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  Label,
  Badge,
  Row,
  Input,
} from "reactstrap"
import "nouislider/distribute/nouislider.css"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useDropzone } from "react-dropzone"
import { uploadImage } from "helpers/UAM/systemConfig.helper"
import {
  addProduct,
  deleteProductImage,
  editProduct,
  getProductDetail,
} from "http/products/products"
import ToastDialog from "components/Common/ToastDialog"
import DeleteModal from "components/Common/DeleteModal"

const AddNewProducts = () => {
  document.title = "Edit Products | DMS"
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const navigate = useNavigate()
  const [product, setProduct] = useState(null)
  const [toastInfo, setToastInfo] = useState({ showToast: false })
  const [deleteModal, setDeleteModal] = useState(false)
  const [idsToDelete, setIdsToDelete] = useState([])
  const [lotSize, setLotSize] = useState(0)

  const validationSchema = Yup.object().shape({
    minQty: Yup.number()
      .typeError("Minimum product quantity must be a number")
      .min(1, "Minimum quantity should not be less than 1")
      .nullable(),
    maxQty: Yup.number()
    .typeError("Maximum product quantity must be a number")
    .min(1, "Maximum quantity should not be less than 1")
    .test('is-nine-digits', 'Maximum quantity should be up to 9 digits', value => {
      if (value === undefined || value === null) return true; // Allow empty value
      return value.toString().length <= 9; // Check if the length is less than or equal to 9
    })
    .nullable(),
    lotSize: Yup.number()
      .typeError("Lot size must be a number")
      .min(1, "lot size should not be less than 1")
      .nullable(),
    images: Yup.array()
      .required("Images is required")
      .min(1, "At least one image is required"),
  })

  const validationProduct = useFormik({
    initialValues: {
      minQty: product?.minQty || null,
      maxQty: product?.maxQty || null,
      lotSize: product?.lotSize || null,
      images: product?.productImages.map(i => i.image),
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      if (!id) {
        try {
          let res = await addProduct(values)
          if (res.status) {
            setToastInfo({
              message: res.message,
              showToast: true,
              role: "success",
            })
            validationProduct.resetForm()
            setTimeout(() => {
              window.history.back()
            }, 1000)
          }
        } catch (err) {
          setToastInfo({
            message: err.message,
            showToast: true,
            role: "error",
          })
          validationProduct.resetForm()
        }
      } else {
        try {
          if (values.minQty) {
            let minValue = lotSize || +values.minQty
            if (minValue >= +values.maxQty) {
              setToastInfo({
                message:
                  "Minimum quantity shouldn't be greater than or equal to maximum quantity",
                showToast: true,
                role: "error",
              })
              return
            }
          }
          if (values.lotSize) {
            if (+values.maxQty % +values.lotSize !== 0) {
              setToastInfo({
                message: "Maximum quantity must be a multiple of the lot size",
                showToast: true,
                role: "error",
              })
              return
            }
          }
          values.lotSize = values.lotSize || null
          values.minQty =
            (values.lotSize ? values.lotSize : values.minQty) || null
          values.maxQty = values.maxQty || null
          const updatedValues = { ...values }
          updatedValues.id = id
          updatedValues.images = updatedValues.images.map(i => {
            let imageId = product.productImages.find(
              img => img.image.paths.original === i.paths.original
            )?.id

            if (imageId) {
              return {
                id: imageId,
                image: { ...i },
              }
            } else {
              return { image: { ...i } }
            }
          })
          await Promise.all(idsToDelete.map(i => deleteProductImage({ id: i })))
          let res = await editProduct(updatedValues)
          if (res.status) {
            setToastInfo({
              message: res.message,
              showToast: true,
              role: "success",
            })
            validationProduct.resetForm()
            setTimeout(() => {
              window.history.back()
            }, 1000)
          }
        } catch (err) {
          setToastInfo({
            message: err.message,
            showToast: true,
            role: "error",
          })
          validationProduct.resetForm()
        }
      }
    },
  })

  const validateFileExtensions = files => {
    const allowedExtensions = [".png", ".jpeg", ".jpg"]
    return files.filter(file => {
      const fileExtension = file.name.split(".").pop().toLowerCase()
      return allowedExtensions.includes(`.${fileExtension}`)
    })
  }

  const handleImageUpload = async selectedFiles => {
    const uploadPromises = selectedFiles.map(file => uploadImage(file))

    try {
      const results = await Promise.all(uploadPromises)

      return results.map((res, index) => ({
        filenames: res[0]?.filenames,
        paths: res[0]?.paths,
        primary:
          (!validationProduct.values.images ||
            validationProduct.values.images.length === 0) &&
          index === 0,
      }))
    } catch (error) {
      console.error("Error uploading images:", error)
      return []
    }
  }

  const onDrop = useCallback(
    async acceptedFiles => {
      const totalFiles = acceptedFiles.length

      if (totalFiles) {
        const filteredFiles = validateFileExtensions(acceptedFiles)

        if (filteredFiles.length !== totalFiles) {
          setToastInfo({
            message: "Only .png, .jpeg, and .jpg files are allowed.",
            showToast: true,
            role: "error",
          })
          return
        }

        const uploadImages = async () => {
          if (
            totalFiles + (validationProduct?.values?.images || []).length >
            5
          ) {
            setToastInfo({
              message: "Maximum 5 images allowed.",
              showToast: true,
              role: "error",
            })
            return
          }

          const newImages = await handleImageUpload(filteredFiles)

          if (
            newImages.length +
              (validationProduct?.values?.images || []).length >
            5
          ) {
            setToastInfo({
              message: "Maximum 5 images allowed.",
              showToast: true,
              role: "error",
            })
            return
          }

          validationProduct.setFieldValue("images", [
            ...(validationProduct?.values?.images || []),
            ...newImages,
          ])
        }

        uploadImages()
      }
    },
    [validationProduct?.values?.images, uploadImage, setToastInfo]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
  })

  const fetchProductDetail = async () => {
    let res = await getProductDetail({ id: id })
    setProduct(res?.data[0])
    setLotSize(res?.data[0]?.lotSize || 0)
  }

  useEffect(() => {
    if (id) {
      fetchProductDetail()
    }
  }, [id])

  useEffect(() => {
    if (product && id) {
      validationProduct.setValues({
        productName: product?.productName || "",
        sku: product?.sku || "",
        productDescription: product?.productDescription || "",
        category: product?.category || "",
        subcategory: product?.subCategory || "",
        sellingPrice: product?.sellingPrice || "",
        images: product?.productImages.map(i => i.image),
        model: product?.model || "",
        baseUnit: product?.baseUnit || "",
        minQty: product?.minQty || null,
        maxQty: product?.maxQty || null,
        lotSize: product?.lotSize || null,
      })
    }
  }, [product])

  const FileUpload = ({ label, imageUrl, primary }) => {
    const handleDelete = () => {
      const wasPrimary = primary
      if (id) {
        const imageIdToDelete = product.productImages.find(
          i => i?.image?.paths.original === imageUrl
        )?.id
        if (imageIdToDelete) {
          setIdsToDelete([...idsToDelete, imageIdToDelete])
        }
      }
      const updatedImages = validationProduct.values.images.filter(
        i => i.paths.original !== imageUrl
      )
      if (wasPrimary && updatedImages.length > 0) {
        updatedImages[0].primary = true
      }
      validationProduct.setFieldValue("images", updatedImages)
    }

    const handleSetPrimary = () => {
      const updatedImages = validationProduct.values.images.map(i => {
        const updatedImage = { ...i }
        updatedImage.primary = i.paths.original === imageUrl
        return updatedImage
      })

      validationProduct.setFieldValue("images", updatedImages)
    }

    return (
      <Col lg={3}>
        <Card
          style={{
            width: "100%",
          }}
          className="custom-card"
        >
          <i
            onClick={handleDelete}
            style={{
              position: "absolute",
              top: "-17px",
              right: "-12px",
              color: "#f46a6a",
              zIndex: 1000,
            }}
            className="delete-bin mdi mdi-close-circle font-size-18"
          ></i>
          <img
            alt="Sample"
            src={
              imageUrl.includes("https://")
                ? imageUrl
                : process.env.REACT_APP_IMAGE_URL + imageUrl
            }
          />
          <CardBody>
            <CardTitle
              tag="h6"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "14px",
              }}
            >
              {label}
            </CardTitle>
            {primary ? (
              <Badge color="success" pill>
                Primary
              </Badge>
            ) : (
              <Badge
                onClick={handleSetPrimary}
                color="primary"
                className="set-as-primary-badge p-1"
                pill
              >
                Set As a Primary
              </Badge>
            )}
          </CardBody>
        </Card>
      </Col>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Products"
            breadcrumbItem={id ? "Edit Product" : "New Product"}
          />
          <Card>
            <CardBody>
              <Form onSubmit={validationProduct.handleSubmit}>
                <Row></Row>
                <Row className="mt-3">
                  <Col sm={12} md={6} xl={4}>
                    <Label
                      htmlFor="dealerType"
                      className="col-sm-6 label-styling"
                    >
                      Product Name
                    </Label>
                    <p>{product?.productName || "-"}</p>
                  </Col>
                  <Col sm={12} md={6} xl={4}>
                    <Label
                      htmlFor="productCode"
                      className="col-sm-6 label-styling"
                    >
                      Product Code
                    </Label>
                    <p>{product?.productCode || "-"}</p>
                  </Col>
                  <Col sm={12} md={6} xl={4}>
                    <Label
                      htmlFor="description"
                      className="col-sm-6 label-styling"
                    >
                      Description
                    </Label>
                    <p>{product?.productDescription || "-"}</p>
                  </Col>
                  <Col sm={12} md={6} xl={4}>
                    <Label
                      htmlFor="description"
                      className="col-sm-6 label-styling"
                    >
                      Category
                    </Label>
                    <p>{product?.categoryCategory?.categoryName || "-"}</p>
                  </Col>
                  <Col sm={12} md={6} xl={4}>
                    <Label
                      htmlFor="description"
                      className="col-sm-6 label-styling"
                    >
                      Subcategory
                    </Label>
                    <p>
                      {product?.subCategorySubCategory?.subCategoryName || "-"}
                    </p>
                  </Col>
                  <Col sm={12} md={6} xl={4}>
                    <Label
                      htmlFor="description"
                      className="col-sm-6 label-styling"
                    >
                      Model
                    </Label>
                    <p>{product?.model?.modelName || "-"}</p>
                  </Col>
                  <Col sm={12} md={6} xl={4}>
                    <Label
                      htmlFor="description"
                      className="col-sm-6 label-styling"
                    >
                      Base Unit
                    </Label>
                    <p>{product?.baseUnit ? product?.baseUnit : "-"}</p>
                  </Col>
                  <Col sm={12} md={6} xl={4}>
                    <Label
                      htmlFor="description"
                      className="col-sm-6 label-styling"
                    >
                      Selling Price
                    </Label>
                    <p>
                      {product?.sellingPrice
                        ? formatNumberWithCommas(product.sellingPrice)
                        : "-"}
                    </p>
                  </Col>
                </Row>
                <div
                  className="mb-4"
                  style={{ borderTop: "1px solid #d3d3d3" }}
                ></div>
                <h4 className="card-title mb-2">Modify Product Details</h4>
                <Row>
                  <Col sm={12} md={6} xl={4} className="mb-2">
                    <Label htmlFor="description" className="col-sm-6">
                      Lot Size
                    </Label>
                    <Input
                      name="lotSize"
                      type="text"
                      placeholder="Lot size"
                      className={`form-control ${
                        validationProduct.touched.lotSize &&
                        validationProduct.errors.lotSize
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={e => {
                        setLotSize(e.target.value)
                        validationProduct.handleChange(e)
                      }}
                      onBlur={validationProduct.handleBlur}
                      value={validationProduct.values.lotSize}
                    />
                    {validationProduct.touched.lotSize &&
                    validationProduct.errors.lotSize ? (
                      <p className="feedback-error">
                        {validationProduct.errors.lotSize}
                      </p>
                    ) : null}
                  </Col>
                  <Col sm={12} md={6} xl={4} className="mb-2">
                    <Label htmlFor="description" className="col-sm-6">
                      Min Quantity
                    </Label>
                    <Input
                      name="minQty"
                      type="text"
                      placeholder="Minimum Quantity"
                      onChange={validationProduct.handleChange}
                      onBlur={validationProduct.handleBlur}
                      value={
                        lotSize > 0 ? lotSize : validationProduct.values.minQty
                      }
                      disabled={lotSize > 0}
                    />
                    {validationProduct.touched.minQty &&
                    validationProduct.errors.minQty ? (
                      <p className="feedback-error">
                        {validationProduct.errors.minQty}
                      </p>
                    ) : null}
                  </Col>
                  <Col sm={12} md={6} xl={4} className="mb-2">
                    <Label htmlFor="maxQty" className="col-sm-6">
                      Max Quantity
                    </Label>
                    <Input
                      name="maxQty"
                      type="text"
                      placeholder="Maximum Quantity"
                      onChange={validationProduct.handleChange}
                      onBlur={validationProduct.handleBlur}
                      value={validationProduct.values.maxQty}
                    />
                    {validationProduct.touched.maxQty &&
                    validationProduct.errors.maxQty ? (
                      <p className="feedback-error">
                        {validationProduct.errors.maxQty}
                      </p>
                    ) : null}
                  </Col>
                </Row>
                <Row className="mt-5">
                  <Col sm={12} lg={5}>
                    <div
                      className="drop"
                      onKeyUp={() => {}}
                      onClick={() => {
                        validationProduct.touched.images = true
                      }}
                    >
                      <div
                        className="dz-message needsclick text-wrapper"
                        {...getRootProps()}
                      >
                        <input
                          type="file"
                          name="formatDocument"
                          id="images"
                          accept=".jpg, .png, .jpeg, .pdf"
                          {...getInputProps()}
                        />
                        <div className="dz-message needsclick">
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="required-sign"
                          >
                            {"Upload File ( images)"}
                            <i className="mdi mdi-asterisk"></i>
                          </Label>
                          <div className="mb-3">
                            <img
                              style={{ width: "32%" }}
                              src="/upload.svg"
                              alt="upload"
                            />
                          </div>
                          <label
                            style={{
                              marginBottom: 0,
                              fontSize: "14px",
                            }}
                          >
                            {isDragActive ? (
                              <p className="textLink">Drop here</p>
                            ) : (
                              <>
                                <span className="textLink1">
                                  Browse files to attach
                                </span>
                                or Drop
                              </>
                            )}
                          </label>
                        </div>
                        <label style={{ fontSize: "14px", marginTop: "10px" }}>
                          Max file size: 2MB. You can add up to 5 images.
                        </label>
                      </div>
                    </div>

                    {validationProduct.submitCount > 0 &&
                    validationProduct.errors.images ? (
                      <p className="feedback-error">
                        {validationProduct.errors.images}
                      </p>
                    ) : null}
                  </Col>
                  <Col sm={12} lg={7}>
                    <Row>
                      {validationProduct?.values?.images?.map(
                        (image, index) => (
                          <FileUpload
                            key={image.id}
                            imageUrl={image.paths.original}
                            label={image?.filenames.original}
                            primary={image?.primary}
                          />
                        )
                      )}
                    </Row>
                  </Col>
                </Row>
                <div className="text-sm-end">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => {
                      navigate(-1)
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-success"
                    style={{ margin: "10px 10px 10px 10px" }}
                    type="submit"
                  >
                    Save
                  </button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>
      <ToastDialog
        message={toastInfo.message}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => {}}
        onCloseClick={() => setDeleteModal(false)}
      />
    </React.Fragment>
  )
}

export default withRouter(AddNewProducts)
